import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminProvider, useAdmin } from './contexts/AdminContext';
import ErrorBoundary from './components/ErrorBoundary';
import { logger } from './utils/logger';
import Header from './components/Header';
import RedirectIfAuthenticated from './components/RedirectIfAuthenticated';
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/profile/ProfilePage';
import ExtendedProfilePage from './pages/profile/ExtendedProfilePage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PublicOfferPage from './pages/PublicOfferPage';
import NatalPage from './pages/NatalPage';
import NumerologyPage from './pages/NumerologyPage';
import ForecastsPage from './pages/ForecastsPage';
import CompatibilityPage from './pages/CompatibilityPage';
import SpecialAnalysesPage from './pages/SpecialAnalysesPage';
import ElectionalPage from './pages/ElectionalPage';
import EclipseForecastPage from './pages/forecasts/EclipseForecastPage';
import MedicalForecastPage from './pages/forecasts/MedicalForecastPage';
import ChildHoroscopePage from './pages/forecasts/ChildHoroscopePage';
import VocationalPage from './pages/forecasts/VocationalPage';
import ArticlesPage from './pages/ArticlesPage';
import ArticleDetailPage from './pages/ArticleDetailPage';
import CompositeChartPage from './pages/compatibility/CompositeChartPage';
import CosmicBackground from './components/CosmicBackground';
import CookieConsent from './components/CookieConsent';
import { yandexMetrica } from './services/metrica';
// Admin pages
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminUserDetailPage from './pages/AdminUserDetailPage';
import QueueDashboardPage from './pages/admin/QueueDashboardPage';
import MetricsDashboardPage from './pages/admin/MetricsDashboardPage';
import AdminSubscriptionsPage from './pages/admin/AdminSubscriptionsPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminUserActivityPage from './pages/admin/AdminUserActivityPage';
import AdminUserEditPage from './pages/AdminUserEditPage';
import AdminTrialsPage from './pages/admin/AdminTrialsPage';
import AdminActivityPage from './pages/admin/AdminActivityPage';
import AdminBroadcastsPage from './pages/admin/AdminBroadcastsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import NotificationSettingsPage from './pages/profile/NotificationSettingsPage';
import NotificationPrompt from './components/NotificationPrompt';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        color: '#d4af37',
        fontSize: '18px',
      }}>
        Загрузка...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAdmin();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white text-lg">
        Загрузка...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
}

function AppContent() {
  // Инициализация Метрики если согласие уже дано (перезагрузка страницы)
  useEffect(() => {
    if (yandexMetrica.checkConsent() && !yandexMetrica.isReady()) {
      yandexMetrica.giveConsent();
    }
  }, []);

  const handleCookieAccept = () => {
    logger.info('Cookie consent accepted');
    yandexMetrica.giveConsent();
    yandexMetrica.reachGoal('cookie_accepted');
  };

  const handleCookieDecline = () => {
    logger.info('Cookie consent declined');
  };

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      fontFamily: 'var(--font-body)',
    }}>
      <CosmicBackground />
      <Header />
      <ErrorBoundary>
        <main style={{ position: 'relative', zIndex: 1 }}>
          <Routes>
          <Route path="/" element={<RedirectIfAuthenticated><HomePage /></RedirectIfAuthenticated>} />
          <Route path="/calendar" element={<HomePage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/profile/extended" element={<ProtectedRoute><ExtendedProfilePage /></ProtectedRoute>} />
          <Route path="/natal" element={<ProtectedRoute><NatalPage /></ProtectedRoute>} />
          <Route path="/numerology" element={<ProtectedRoute><NumerologyPage /></ProtectedRoute>} />
          <Route path="/forecasts" element={<ProtectedRoute><ForecastsPage /></ProtectedRoute>} />
          <Route path="/compatibility" element={<ProtectedRoute><CompatibilityPage /></ProtectedRoute>} />
          <Route path="/compatibility/composite" element={<ProtectedRoute><CompositeChartPage /></ProtectedRoute>} />
          <Route path="/analyses" element={<ProtectedRoute><SpecialAnalysesPage /></ProtectedRoute>} />
          <Route path="/electional" element={<ProtectedRoute><ElectionalPage /></ProtectedRoute>} />
          <Route path="/forecasts/eclipses" element={<ProtectedRoute><EclipseForecastPage /></ProtectedRoute>} />
          <Route path="/forecasts/medical" element={<ProtectedRoute><MedicalForecastPage /></ProtectedRoute>} />
          <Route path="/forecasts/child" element={<ProtectedRoute><ChildHoroscopePage /></ProtectedRoute>} />
          <Route path="/forecasts/vocational" element={<ProtectedRoute><VocationalPage /></ProtectedRoute>} />
          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/:slug" element={<ArticleDetailPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/public-offer" element={<PublicOfferPage />} />
          {/* Admin routes */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
          <Route path="/admin/users/:id" element={<AdminRoute><AdminUserDetailPage /></AdminRoute>} />
          <Route path="/admin/queue" element={<AdminRoute><QueueDashboardPage /></AdminRoute>} />
          <Route path="/admin/metrics" element={<AdminRoute><MetricsDashboardPage /></AdminRoute>} />
          <Route path="/admin/subscriptions" element={<AdminRoute><AdminSubscriptionsPage /></AdminRoute>} />
          <Route path="/admin/payments" element={<AdminRoute><AdminPaymentsPage /></AdminRoute>} />
          <Route path="/admin/users/:id/activity" element={<AdminRoute><AdminUserActivityPage /></AdminRoute>} />
          <Route path="/admin/users/:id/edit" element={<AdminRoute><AdminUserEditPage /></AdminRoute>} />
          <Route path="/admin/trials" element={<AdminRoute><AdminTrialsPage /></AdminRoute>} />
          <Route path="/admin/activity" element={<AdminRoute><AdminActivityPage /></AdminRoute>} />
          <Route path="/admin/broadcasts" element={<AdminRoute><AdminBroadcastsPage /></AdminRoute>} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/delete-account" element={<ProtectedRoute><DeleteAccountPage /></ProtectedRoute>} />
          <Route path="/profile/notifications" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
        </Routes>
      </main>
      </ErrorBoundary>
      <CookieConsent onAccept={handleCookieAccept} onDecline={handleCookieDecline} />
      <NotificationPrompt />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminProvider>
          <AppContent />
        </AdminProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
